import { createTheme } from "@mui/material/styles";
import { themeFonts } from "./themeFonts.const";

const baseTheme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 480,
            md: 768,
            lmd: 1024,
            lg: 1280,
            xl: 1440,
        },
    },
    palette: {
        text: {
            primary: "#2D3137",
            secondary: "#fff",
            tertiary: "rgba(45, 49, 55, 0.8)",
            reviews: "#9298B8",
            quaternary: "#72789A",
            disabled: "#B2B2B2",
            black: "#000",
            error: "#BF5252",
            yellow: "#F8DB46",
            darkGrey: "#474747",
        },
        background: {
            default: "#EFEFEF",
            primary: "#2D3137",
            secondary: "#000",
            tertiary: "#000",
            quaternary: "#fff",
            whiteOpacity4: "rgba(255, 255, 255, .4)",
            grey: "#141414",
            whiteOpacity2: "rgba(255, 255, 255, .2)",
            primaryLight: "#EFEFEF",
            brightGreen: "#5FCB39",
            lightGrey: "rgba(45, 49, 55, 0.05)",
            lightAccent: "#f3f4f7",
            darkGrey: "#1E1D1D",
            buttonHoverBg: "#67BF95",
            borderLightGrey: "#C2C2C2",
            green: "#1F894B",
        },
        brandColors: {
            brandGreen: "#58B287",
            brandBlack: "#2D3137",
            brandWhite: "#EFEFEF",
            brandLightgrey: "#CBCBCB",
        },
    },
});

export const theme = createTheme(baseTheme, {
    typography: {
        h1: {
            fontFamily: themeFonts.sourceCodePro,
            fontSize: "34px",
            lineHeight: 1.285,
            fontWeight: 700,
        },
        h3: {
            fontFamily: themeFonts.salara,
            fontSize: "28px",
            lineHeight: 1.285,
            fontWeight: 700,
        },
        title2Regular: {
            fontFamily: themeFonts.salara,
            fontSize: "22px",
            lineHeight: 1.333,
            fontWeight: 400,
        },
        titleBold: {
            fontFamily: themeFonts.sourceCodePro,
            fontSize: "24px",
            lineHeight: 1.333,
            fontWeight: 800,
        },
        title1Bold: {
            fontFamily: themeFonts.sourceCodePro,
            fontSize: "22px",
            lineHeight: 1.333,
            fontWeight: 700,
        },
        title3Bold: {
            fontFamily: themeFonts.inter,
            fontSize: "20px",
            lineHeight: 1.22,
            fontWeight: 700,
        },
        body1Bold: {
            fontFamily: themeFonts.geologica,
            fontSize: "20px",
            lineHeight: 1.2,
            fontWeight: 700,
        },
        body2Regular: {
            fontFamily: themeFonts.salara,
            fontSize: "16px",
            lineHeight: 1.5,
            fontWeight: 400,
        },
        body2Medium: {
            fontFamily: themeFonts.salara,
            fontSize: "16px",
            lineHeight: 1.5,
            fontWeight: 500,
        },
        body2Semibold: {
            fontFamily: themeFonts.sourceCodePro,
            fontSize: "16px",
            lineHeight: 1.5,
            fontWeight: 600,
        },
        bodyInterExtraBold: {
            fontFamily: themeFonts.inter,
            fontSize: "17px",
            lineHeight: 1.4,
            fontWeight: 800,
        },
        body1Medium: {
            fontFamily: themeFonts.geologica,
            fontSize: "22px",
            lineHeight: 1.428,
            fontWeight: 500,
        },
        body1CaptionBold: {
            fontFamily: themeFonts.salara,
            fontSize: "16px",
            lineHeight: 1.333,
            fontWeight: 700,
        },
        smallRegular: {
            fontFamily: themeFonts.salara,
            fontSize: "14px",
            lineHeight: 1.333,
            fontWeight: 400,
        },
        smallThin: {
            fontFamily: themeFonts.geologica,
            fontSize: "11px",
            lineHeight: 1.333,
            fontWeight: 300,
        },
        smallBold: {
            fontFamily: themeFonts.salara,
            fontSize: "14px",
            lineHeight: 1.333,
            fontWeight: 700,
        },
        smallInterRegular: {
            fontFamily: themeFonts.inter,
            fontSize: "14px",
            lineHeight: 1.22,
            fontWeight: 400,
        },
        smallInterMedium: {
            fontFamily: themeFonts.inter,
            fontSize: "14px",
            lineHeight: 1.22,
            fontWeight: 500,
        },
        footnoteRegular: {
            fontFamily: themeFonts.salara,
            fontSize: "11px",
            lineHeight: "16px",
            fontWeight: 400,
        },
        dmSansH3Semibold: {
            fontFamily: themeFonts.dmSans,
            fontSize: "48px",
            lineHeight: "50px",
            fontWeight: 600,
        },
        dmSansH4Medium: {
            fontFamily: themeFonts.dmSans,
            fontSize: "34px",
            lineHeight: "40px",
            fontWeight: 500,
        },
        dmSansH5Regular: {
            fontFamily: themeFonts.dmSans,
            fontSize: "23px",
            lineHeight: "32px",
            fontWeight: 400,
        },
        dmSansH4H5Semibold: {
            fontFamily: themeFonts.dmSans,
            fontSize: "28px",
            lineHeight: "32px",
            fontWeight: 600,
        },
        dmSansH4Semibold: {
            fontFamily: themeFonts.dmSans,
            fontSize: "34px",
            lineHeight: "40px",
            fontWeight: 600,
            letterSpacing: "0.25px",
        },
        dmSansH5Semibold: {
            fontFamily: themeFonts.dmSans,
            fontSize: "23px",
            lineHeight: "32px",
            fontWeight: 600,
        },
        dmSansBody1Regular: {
            fontFamily: themeFonts.dmSans,
            fontSize: "16px",
            lineHeight: "23px",
            fontWeight: 400,
        },
        dmSansBody2Regular: {
            fontFamily: themeFonts.dmSans,
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: 400,
        },
        dmSansBody2Semibold: {
            fontFamily: themeFonts.dmSans,
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: 600,
        },
    },
    components: {
        MuiPopper: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "&.popper--places-autocomplete": {
                        boxShadow: "var(--mui-shadows-2)",
                        zIndex: "calc(var(--mui-zIndex-appBar) - 10)",
                        maxWidth: "350px",
                        borderRadius: "12px",
                        marginTop: "2px !important",
                        "& .paper--places-autocomplete": {
                            boxShadow: "none",
                            backgroundColor: "transparent",
                        },
                        "& .MuiList-root": {
                            margin: 0,
                            padding: 0,
                            boxShadow: "none",
                            borderRadius: "12px",
                            maxHeight: "210px !important",
                            backgroundColor: "rgba(243, 244, 247, 0.95)",
                            overflow: "auto",
                        },
                        "& .MuiMenuItem-root": {
                            width: "100%",
                            maxWidth: "350px",
                            padding: "13px !important",
                            overflow: "hidden",
                            "&:hover": {
                                backgroundColor: theme.palette.background.buttonHoverBg,
                                color: theme.palette.text.secondary,
                            },
                            "&:not(:last-child)": {
                                borderBottom: `0.5px solid ${theme.palette.brandColors.brandLightgrey}`,
                            },
                            ".MuiTypography-root": {
                                fontFamily: themeFonts.salara,
                                fontSize: "14px",
                                lineHeight: 1.333,
                                fontWeight: 400,
                            },
                        },
                    },
                }),
            },
        },
        MuiSvgIcon: {
            variants: [
                {
                    props: { fontSize: "large" },
                    style: {
                        fontSize: "2rem",
                    },
                },
            ],
        },
        MuiButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "&:disabled": {
                        background: "rgba(88, 178, 135, 0.3)",
                        color: theme.palette.text.secondary,
                    },
                    "&:hover": {
                        background: theme.palette.background.buttonHoverBg,
                        color: theme.palette.text.secondary,
                    },
                }),
            },

            variants: [
                {
                    props: { variant: "themePrimary" },
                    style: ({ theme }) => ({
                        backgroundColor: theme.palette.brandColors.brandGreen,
                        fontFamily: themeFonts.sourceCodePro,
                        color: theme.palette.text.secondary,
                        fontWeight: 800,
                        fontSize: "24px",
                        padding: "18px 130px",
                        borderRadius: "16px",
                        height: "67px",
                    }),
                },
                {
                    props: { variant: "themePrimarySmall" },
                    style: ({ theme }) => ({
                        backgroundColor: theme.palette.brandColors.brandGreen,
                        color: theme.palette.text.secondary,
                        fontWeight: 800,
                        fontSize: "16px",
                        padding: "12px 40px",
                        borderRadius: "12px",
                    }),
                },
                {
                    props: { variant: "xs" },
                    style: ({ theme }) => ({
                        backgroundColor: theme.palette.brandColors.brandGreen,
                        color: theme.palette.text.secondary,
                        fontWeight: 700,
                        fontSize: "12px",
                        padding: ".25rem .5rem",
                        borderRadius: "6px",
                        "&.disabled-active-view:disabled": {
                            color: "#fff !important",
                        },
                    }),
                },
                {
                    props: { variant: "outlined" },
                    style: ({ theme }) => ({
                        backgroundColor: "transparent",
                        color: theme.palette.text.secondary,
                        fontWeight: 800,
                        fontFamily: themeFonts.sourceCodePro,
                        fontSize: "16px",
                        padding: "14px 40px",
                        borderRadius: "12px",
                        border: `1px solid ${theme.palette.brandColors.brandGreen}`,
                        "&:hover": {
                            borderColor: theme.palette.brandColors.brandGreen,
                        },
                    }),
                },
                {
                    props: { variant: "gradient" },
                    style: ({ theme }) => ({
                        height: "60px",
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        fontFamily: themeFonts.dmSans,
                        fontSize: "23px",
                        textTransform: "capitalize",
                        padding: "15px 32px",
                        background: "linear-gradient(276deg, #1F894B 44.33%, #27AE60 98.56%)",
                        borderRadius: "20px",
                        boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                        transition: "0s",
                        "&:hover": {
                            background: "linear-gradient(276deg, #27AE60 44.33%, #1F894B 98.56%)",
                            boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.25)",
                        },
                        "&:disabled": {
                            cursor: "not-allowed",
                            background: "linear-gradient(276deg, #1F894B 44.33%, #27AE60 98.56%)",
                        },
                    }),
                },
            ],
        },
    },
});
